import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Alert } from "reactstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AppCTA from "@components/AppCTA/AppCTA";
import GeneralConst from "@consts/GeneralConst";
import logoBlack from "@images/logos/logoBlack.png";
import MailService from "@services/MailService";
import AlertStrings from "@strings/AlertStrings.json";
import GeneralStrings from "@strings/GeneralStrings.json";
import {
  validateEmail,
  validateMessage,
  validateName,
  validateSubject,
} from "@utilities/ValidationHelper";

interface ContactFormValues {
  email: string;
  name: string;
  message: string;
  subject: string;
}

const INITIAL_VALUES: ContactFormValues = {
  email: "",
  name: "",
  message: "",
  subject: "",
};

const ContactForm = () => {
  const [sentMessage, setSentMessage] = useState<string | false>(false);
  const [sentMessageOpen, setSentMessageOpen] = useState(false);
  const [failedMessage, setFailedMessage] = useState<string | false>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const STATUS_CODES = GeneralConst.STATUS_CODES;
  const { contact } = GeneralStrings;
  const { form } = GeneralStrings;

  const resetAlerts = useCallback(() => {
    setSentMessage(false);
    setSentMessageOpen(false);
    setFailedMessage(false);
  }, []);

  useEffect(() => {
    if (!sentMessage) return;
    const fadeTimer = setTimeout(() => setSentMessageOpen(false), 5000);
    const clearTimer = setTimeout(() => setSentMessage(false), 5300);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
    };
  }, [sentMessage]);

  const handleSubmit = useCallback(
    async (
      formValues: ContactFormValues,
      { resetForm }: FormikHelpers<ContactFormValues>,
    ) => {
      if (!executeRecaptcha) return;

      resetAlerts();

      const recaptchaToken = await executeRecaptcha("contact_form");

      try {
        const response = await MailService.submitContactForm({
          ...formValues,
          recaptchaToken,
        });

        if (response.data.status === STATUS_CODES.ERROR) {
          setFailedMessage(
            response?.data?.statusMessage || AlertStrings.generalError,
          );
        } else {
          const msg =
            response?.data?.statusMessage || AlertStrings.generalSuccess;
          setSentMessage(msg);
          setSentMessageOpen(true);
          resetForm();
        }
      } catch (response: unknown) {
        const errorResponse = response as { data?: { statusMessage?: string } };
        setFailedMessage(
          errorResponse?.data?.statusMessage || AlertStrings.generalError,
        );
      }
    },
    [executeRecaptcha, resetAlerts, STATUS_CODES.ERROR],
  );

  return (
    <section className="section contact" id="contact">
      <div className="container-fluid">
        <div className="title-header">
          <h2>{contact.title}</h2>
          <div className="separator">
            <img src={logoBlack} className="seperator-img" alt="logo" />
          </div>
        </div>

        <div className="row contact-body">
          <div className="col-lg-5 col-xs-12 contact-info">
            <h2 className="contact-headline">
              Need <em>help</em> or have <em>questions?</em>
            </h2>
            <p className="contact-description">{contact.description}</p>
          </div>

          <div className="col-lg-7 col-xs-12">
            <div className="contact-form">
              {failedMessage && <Alert color="danger">{failedMessage}</Alert>}
              {sentMessage && (
                <Alert isOpen={sentMessageOpen} fade>
                  {sentMessage}
                </Alert>
              )}

              <Formik initialValues={INITIAL_VALUES} onSubmit={handleSubmit}>
                {({ submitForm }) => (
                  <Form>
                    <div className="form-field">
                      <label className="field-label">name</label>
                      <Field
                        name="name"
                        className="form-control"
                        placeholder={form.formLabels.yourName}
                        validate={validateName}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="name"
                      />
                    </div>

                    <div className="form-field">
                      <label className="field-label">email</label>
                      <Field
                        name="email"
                        className="form-control"
                        placeholder={form.formLabels.yourEmail}
                        validate={validateEmail}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="email"
                      />
                    </div>

                    <div className="form-field">
                      <label className="field-label">subject</label>
                      <Field
                        name="subject"
                        className="form-control"
                        placeholder={form.formLabels.subject}
                        validate={validateSubject}
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="subject"
                      />
                    </div>

                    <div className="form-field">
                      <label className="field-label">message</label>
                      <Field
                        as="textarea"
                        name="message"
                        className="form-control"
                        placeholder={form.formLabels.message}
                        validate={validateMessage}
                        rows={5}
                        type="textarea"
                      />
                      <ErrorMessage
                        component="span"
                        className="form-error"
                        name="message"
                      />
                    </div>

                    <div className="form-field">
                      <AppCTA
                        type="button"
                        className="btn-contact-submit"
                        label={<span className="submit-text">{form.submit}</span>}
                        onClick={async () => {
                          await submitForm();
                        }}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
